
import { DictionaryEntry } from '@sdbn/common';
import { MasterDetailListEntry } from '@sdbn/common';
import { v4 as uuidv4 } from 'uuid';

export enum TransportOptionConstraintType {
  MIN_SELECTED_COUNT = 'MIN_SELECTED_COUNT',
  MAX_SELECTED_COUNT = 'MAX_SELECTED_COUNT'
}

export class TransportOptionConstraint implements MasterDetailListEntry {
  id: number;
  name: string;
  count: number;
  type = TransportOptionConstraintType.MIN_SELECTED_COUNT;
  transportOptionIds = new Array<number>();
  deleted = false;

  constructor(constraint: TransportOptionConstraint) {
    this.id = constraint.id;
    this.name = constraint.name;
    this.count = constraint.count;
    this.type = constraint.type;
    if (constraint.transportOptionIds != null) {
      this.transportOptionIds.push(...constraint.transportOptionIds);
    }
    this.deleted = constraint.deleted;
  }

  public get listTitle(): string {
    return this.name;
  }

  public get listTitleDictionaryKey(): string {
    return null;
  }

  public get listSubTitle(): string {
    return null;
  }

  public get groupIdentifier(): String {
    return null;
  }

  public get childs(): Array<MasterDetailListEntry> {
    return null;
  }
}

export class EditableTransportOptionConstraint extends TransportOptionConstraint {

  invalid: boolean;

  constructor(constraint: any) {
    super(constraint);
    this.invalid = constraint.invalid;
  }

}

export class TransportOption {

  id: number;
  parentId: number;
  name = new DictionaryEntry();
  hintDriver = new DictionaryEntry();
  allwaysShowHint: boolean;
  optional: boolean;
  permittedProcessingTime: number;
  rowIndex: number;
  deleted = false;
  columnIndex: number;
  externalKey: string;
  mark: string;
  onlyInternal = false;
  noProcessingTimeThreshold = false;
  minimumSelectedOptions: number;
  timeSlotPriority = 0;
  color: string;
  globalConnectNotificationsEnabled: boolean;
  amountOfPallettesMandatory: boolean;
  amountOfBoxesMandatory: boolean;
  amountInKilogrammMandatory: boolean;
  amountInLiterMandatory: boolean;
  amountOthersMandatory: boolean;
  amountMandatoryInTerminal: boolean;
  amountMandatoryInTime: boolean;

  hasParameter: boolean = false;
  parameterName = new DictionaryEntry();
  parameterValues = new Array<TransortOptionParameterValue>();

  bookingOffsetHours: number;
  bookingOffsetTime: string;
  bookingOffsetDays: number;
  bookingOffsetType: BookingOffsetType;

  preventGapsOnMultipleSlots: boolean;

  resources = new Array<string>();

  constructor(option?: any) {
    if (option) {
      this.merge(option);
    }
  }

  public merge(option: Partial<TransportOption>) {
    this.id = option.id;
    this.parentId = option.parentId;
    this.name = new DictionaryEntry(option.name);
    this.hintDriver = new DictionaryEntry(option.hintDriver);
    this.optional = option.optional;
    this.permittedProcessingTime = option.permittedProcessingTime;
    this.rowIndex = option.rowIndex;
    this.deleted = option.deleted;
    this.columnIndex = option.columnIndex;
    this.externalKey = option.externalKey;
    this.mark = option.mark;
    this.onlyInternal = option.onlyInternal;
    this.noProcessingTimeThreshold = option.noProcessingTimeThreshold;
    this.minimumSelectedOptions = option.minimumSelectedOptions || 0;
    this.timeSlotPriority = option.timeSlotPriority || 0;
    this.color = option.color;
    this.globalConnectNotificationsEnabled = option.globalConnectNotificationsEnabled;
    this.amountOfPallettesMandatory = option.amountOfPallettesMandatory;
    this.amountOfBoxesMandatory = option.amountOfBoxesMandatory;
    this.amountInKilogrammMandatory = option.amountInKilogrammMandatory;
    this.amountInLiterMandatory = option.amountInLiterMandatory;
    this.amountOthersMandatory = option.amountOthersMandatory;
    this.amountMandatoryInTerminal = option.amountMandatoryInTerminal;
    this.amountMandatoryInTime = option.amountMandatoryInTime;

    this.hasParameter = option.hasParameter;
    this.parameterName = new DictionaryEntry(option.parameterName);
    this.preventGapsOnMultipleSlots = option.preventGapsOnMultipleSlots;
    this.bookingOffsetHours = option.bookingOffsetHours;
    this.bookingOffsetTime = option.bookingOffsetTime;
    this.bookingOffsetDays = option.bookingOffsetDays;
    this.bookingOffsetType = option.bookingOffsetType;
    this.allwaysShowHint = option.allwaysShowHint;

    this.resources = new Array<string>();
    if (option.resources != null) {
      for (const resource of option.resources) {
        this.resources.push(resource);
      }
    }

    if (option.parameterValues != null) {
      for (const value of option.parameterValues) {

        if (value.deleted && value.id < 0) {
          // entferne noch nicht persistierte Einträge
          const deletedIndex = this.parameterValues.findIndex(v => v.id == value.id);
          this.parameterValues.splice(deletedIndex, 1);
        } else {
          const existingValue = this.parameterValues.find(v => v.id == value.id);
          if (existingValue != null) {
            existingValue.merge(value);
          } else {
            this.parameterValues.push(new TransortOptionParameterValue(value));
          }
        }
      }
    }
  }
}

export class TransportOptionConfiguration {

  transportOptions = new Array<TransportOption>();

  constraints = new Array<TransportOptionConstraint>();

  constructor(configuration: TransportOptionConfiguration) {
    for (const o of configuration.transportOptions) {
      this.transportOptions.push(new TransportOption(o));
    }

    for (const c of configuration.constraints) {
      this.constraints.push(new TransportOptionConstraint(c));
    }
  }

}

export class TransortOptionParameterValue {
  id: number;
  value = new DictionaryEntry();
  deleted: boolean;

  constructor(value?: any) {
    this.merge(value);
  }

  public merge(value?: any) {
    if (value != null) {
      this.id = value.id;
      this.value = value.value == null ? new DictionaryEntry() : new DictionaryEntry(value.value);
      this.deleted = value.deleted;
    }
  }
}

export class OptionsColumn {
  columnIndex: number;
  options = new Array<StructuredTransportOption>();
}

/**
 * Modellerweiterung zur Abbildung der Struktur im Editor
 */
export class StructuredTransportOption extends TransportOption {
  uuid: string;
  children: StructuredTransportOption[] = [];
  constructor(option?: any) {
    super(option);
    if (option) {
      this.uuid = option.uuid || uuidv4();
      if (option.children != null) {
        for (const child of option.children) {
          this.children.push(new StructuredTransportOption(child));
        }
      }
    } else {
      this.uuid = uuidv4();
    }
  }
}

export enum BookingOffsetType {
  NONE = 'NONE',  HOURS = 'HOURS', TIME_AND_WORKDAYS = 'TIME_AND_WORKDAYS'
}
